import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { DataCard, Pill, Text, Link } from "@foris/avocado-suite";
import { CATEGORIES } from "@utils/catalogsCategories";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import styles from "./resourceItem.module.scss";
import useContextUrl from "@common/hooks/useContextUrl";

interface CatalogItemProps {
  data: {
    label: string;
    path: string;
    category: string;
    description?: string;
    isDownloadFile?: boolean;
  };
}

const ResourceItem: FC<CatalogItemProps> = ({ data }) => {
  const { scenario, origin } = useContextUrl();
  const { getDarwinUrl } = useNavigationUrl();
  const { t } = useTranslation();

  const urlParams = `?scenario_id=${scenario}${origin ? `&origin_id=${origin}` : ""}`;
  const openMoreInfo = () => {
    !!data?.path && window?.open(`${getDarwinUrl()}/${data?.path}${urlParams}`, "_blank");
  };

  const categoryColorMapping = {
    [CATEGORIES().students]: { color: "palette-1-50-20", textColor: "palette-1-80" },
    [CATEGORIES().teachers]: { color: "palette-2-50-20", textColor: "palette-2-80" },
    [CATEGORIES().infrastructure]: { color: "palette-3-50-20", textColor: "palette-3-80" },
    [CATEGORIES().academic_offer]: { color: "palette-4-50-20", textColor: "palette-4-80" },
    [CATEGORIES().programming_variables]: { color: "palette-6-50-20", textColor: "palette-6-80" },
  } as const;

  type HeaderStatusColor =
    | "palette_1_50"
    | "palette_2_50"
    | "palette_3_50"
    | "palette_4_50"
    | "palette_6_50";

  const headerColorMapping: Record<string, { headerStatusColor: HeaderStatusColor }> = {
    [CATEGORIES().students]: { headerStatusColor: "palette_1_50" },
    [CATEGORIES().teachers]: { headerStatusColor: "palette_2_50" },
    [CATEGORIES().infrastructure]: { headerStatusColor: "palette_3_50" },
    [CATEGORIES().academic_offer]: { headerStatusColor: "palette_4_50" },
    [CATEGORIES().programming_variables]: { headerStatusColor: "palette_6_50" },
  } as const;

  const chooseStatusColor = {
    headerStatusColor: headerColorMapping[data?.category]?.headerStatusColor,
  };

  const chooseBgColorAndTextColor = {
    color: categoryColorMapping[data?.category]?.color,
    textColor: categoryColorMapping[data?.category]?.textColor,
  } as const;

  return (
    <div className={styles.resourceItemButton} role="button" onClick={openMoreInfo}>
      <DataCard
        {...chooseStatusColor}
        topContent={<Pill {...chooseBgColorAndTextColor}>{data?.category}</Pill>}
        className={styles.resourceItem}
        variant="neutral"
        hasTopBar
        title={data.label}
        description={data?.description ?? undefined}
      >
        <div>
          {data?.isDownloadFile && (
            <div className={styles.resourceItem_containerDownloadFile}>
              <Text className={styles.label} type="sm">
                {t("report.label-excel")}
              </Text>
              <Link iconLeft="copy-file" href="#">
                {t("report.link-excel")}
              </Link>
            </div>
          )}
        </div>
      </DataCard>
    </div>
  );
};

export default ResourceItem;
